// The fake api's live venue (spec/13 §3 Floor, spec/13 §5 Sessions; F-STN-14 … F-STN-18, F-SES-14/-16/
// -17/-20/-21/-25), past the console gate in `fake-server.ts`: a busy Saturday evening at Playground
// Central — sixteen stations across three classes and two zones, in every state a tile can wear (in
// session, ending soon, overdue, screen locked, free, offline, never seen, maintenance, out of order,
// updating, pairing), the sessions running on them with their plans and timelines, two open calls for
// help, the kitchen's active tickets and the business day's takings.
//
// It serves every read the Floor makes — its tiles and List view, and the station modal with its Session tab:
//
//   GET …/venues/{v}/floor                      the live snapshot                       (getFloor)
//   GET …/venues/{v}/stations                   the stations, paginated                 (listStations)
//   GET …/venues/{v}/station-classes            the classes, paginated                  (listStationClasses)
//   GET …/venues/{v}/zones                      the zones, paginated                    (listZones)
//   GET …/venues/{v}/stations/{s}/session       the station's session, or 404 when free (getStationSession)
//   GET …/sessions/{id}                         a session's status and time left        (getSessionStatus)
//   GET …/sessions/{id}/timeline                its segments and event history          (getSessionTimeline)
//   GET …/venues/{v}/calls                      open and recent calls, newest first     (listCalls)
//   GET …/venues/{v}/kitchen/tickets            the kitchen's tickets                   (listKitchenTickets)
//   GET …/venues/{v}/day-close                  the business day's takings              (getBusinessDayClose)
//   GET …/venues/{v}/floor/live                 the floor's live figures (M-01 takings) (getFloorLive)
//   GET …/venues/{v}/stations/{s}/health        a station's open alerts and last sample (getStationHealth)
//   GET …/venues/{v}/stations/{s}/telemetry     its samples over the last hours         (getStationTelemetry)
//   GET …/venues/{v}/needs-attention            open alerts, failed/expired commands    (getNeedsAttention)
//
// and the simple writes: answering and resolving a call, and a session's comp, sell-time, message and
// end. The station commands (power, lock, maintenance, message) are left to `fake-server.ts`, which
// already answers them for any station.
//
// Money is integer paise with its currency (spec/06 §2): the pay-as-you-go rates are whole paise a
// minute, and every session's `charged.chargedAmount` is the sum of its segments' `chargeAmount`. Times
// are RFC 3339 UTC around 14:00Z — 19:30 in the venue's Asia/Kolkata. Each body is checked against the
// contract's own response schema in `__tests__/fake-venue-floor.test.ts`.
//
// No value is imported from `fake-server.ts` (which will import this file): the organisation and venue
// ids are re-declared below and must match `ORGANIZATION.id` and `VENUE_ID` there.

import type {
  DayCloseReview,
  FloorStationHealth,
  FloorStationState,
  NeedsAttention,
  StationAlert,
  StationTelemetrySample,
  KitchenTicket,
  Problem,
  Session,
  SessionEvent,
  SessionLedgerEntry,
  SessionSegment,
  SessionTimeline,
  Station,
  StationCall,
  StationClass,
  Zone,
} from '@clavian/api-client';
import { ClearStationAlertsRequestSchema, CompSessionSchema, MessageSessionSchema, PageQuerySchema, SellSessionTimeSchema, type z } from '@clavian/contracts';

import { quoteConsoleSession, type ConsoleOffer, type ConsoleQuote, type ConsoleRate } from '@clavian/domain/console-pricing';

import type { FakeResponse } from './fake-server';
import { FAKE_PLAYER_IDS } from './fake-venue-players';

/** `ORGANIZATION.id` in fake-server.ts. */
export const FLOOR_ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
/** `VENUE_ID` in fake-server.ts (slug `playground-central`). */
export const FLOOR_VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';

/** The api's clock for every fixture: 19:30 in Kolkata on a Saturday. */
export const FLOOR_NOW = '2026-09-26T14:00:00.000Z';
const CREATED = '2026-08-01T06:30:00.000Z';
const INR = 'INR';

/** A TypeID from the fixtures' shared body: a prefix, 23 fixed characters and a 3-character tail. */
const BODY = '01k5znw4hmfay8vvr14d2pf';
const tid = (prefix: string, tail: string): string => `${prefix}_${BODY}${tail}`;
const two = (n: number): string => String(n).padStart(2, '0');

/** `2026-09-26T{hh:mm}:00.000Z`. */
const at = (hhmm: string): string => `2026-09-26T${hhmm}:00.000Z`;
const plusSeconds = (iso: string, seconds: number): string => new Date(Date.parse(iso) + seconds * 1000).toISOString();
const secondsBetween = (from: string, to: string): number => Math.round((Date.parse(to) - Date.parse(from)) / 1000);
const money = (amount: number) => ({ amount, currency: INR });

export interface FloorHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  /** Whether the signed-in member holds a permission on the server (the clear endpoint's gate, F-STN-11). */
  readonly can?: (permission: string) => boolean;
  /** A booked console session ended (ADR-0034): place its one order; returns the sale's id. */
  readonly onConsoleOrder?: (order: FakeConsoleOrder) => string;
  /** Whether a seeded station runs the Clavian Station app (`station`, the default) or none (`none`, a Clavian Boot only PC). */
  readonly clientOf?: (number: number) => 'station' | 'none';
  /** The client of a station made from a new PC (Make it station). */
  readonly madeClient?: () => 'station' | 'none';
  /** A Boot-only venue: only the PCs are on the floor (a console never starts from the network). */
  readonly pcsOnly?: boolean;
}

/** What Make it station asks of the floor. */
export interface FakeMadeStation {
  readonly number: number;
  readonly classId: string;
  readonly zoneId?: string;
  readonly name?: string;
}

/** The order a console session places when it ends (ADR-0034), for the commerce fake's awaiting queue. */
export interface FakeConsoleOrder {
  readonly sessionId: string;
  readonly stationId: string;
  readonly playerId: string | null;
  readonly lines: readonly { readonly name: string; readonly unitPrice: number; readonly quantity: number }[];
  /** The console offers' discount, in paise. */
  readonly discount: number;
}

/** The nil player id a walk-in's session carries on the wire (ADR-0034, `WALK_IN_PLAYER_ID`). */
const WALK_IN = 'pla_00000000000000000000000000';

// -----------------------------------------------------------------------------------------------
// Classes and zones
// -----------------------------------------------------------------------------------------------

const scope = { organizationId: FLOOR_ORGANIZATION_ID, venueId: FLOOR_VENUE_ID, createdAt: CREATED, updatedAt: CREATED } as const;

export const FLOOR_CLASSES: readonly StationClass[] = [
  {
    ...scope,
    id: tid('scl', 'b01'),
    name: 'Standard PC',
    colorToken: 'category-1',
    description: 'RTX 4060 PCs at 165 Hz — the everyday floor.',
    ageLimit: null,
    pricingMode: 'per_player',
    defaultCapacity: 1,
  },
  {
    ...scope,
    id: tid('scl', 'b02'),
    name: 'Pro Arena',
    colorToken: 'category-3',
    description: 'RTX 4080 rigs at 360 Hz with pro peripherals, for ranked play.',
    ageLimit: 16,
    pricingMode: 'per_player',
    defaultCapacity: 1,
  },
  {
    ...scope,
    id: tid('scl', 'b03'),
    name: 'PS5 Lounge',
    colorToken: 'category-5',
    description: 'PlayStation 5 on a 65" screen with a sofa for two.',
    ageLimit: null,
    pricingMode: 'per_station',
    defaultCapacity: 2,
  },
];

export const FLOOR_ZONES: readonly Zone[] = [
  { ...scope, id: tid('zon', 'c01'), name: 'Main hall', colorToken: 'category-2', floor: 0 },
  { ...scope, id: tid('zon', 'c02'), name: 'Pro arena', colorToken: 'category-4', floor: 1 },
];

const [STANDARD, PRO, LOUNGE] = FLOOR_CLASSES as [StationClass, StationClass, StationClass];
const [MAIN_HALL, ARENA] = FLOOR_ZONES as [Zone, Zone];

/** Pay-as-you-go by class, in whole paise a minute: ₹60, ₹120 and ₹180 an hour. */
const RATE_PER_MINUTE: Readonly<Record<string, number>> = { [STANDARD.id]: 100, [PRO.id]: 200, [LOUNGE.id]: 300 };

/**
 * The PS5 lounge's controller pricing (ADR-0034): ₹120 an hour the first controller, ₹60 each extra; 1 h = ₹100 and
 * 2 h = ₹180 fixed; Open rounds up to 15 minutes. Priced by the domain's one `quoteConsoleSession`, as the api does.
 */
export const FLOOR_CONSOLE_RATE: ConsoleRate = {
  firstControllerHourly: { amount: 12_000, currency: INR },
  extraControllerHourly: { amount: 6_000, currency: INR },
  blockPrices: [
    { seconds: 3600, amount: { amount: 10_000, currency: INR } },
    { seconds: 7200, amount: { amount: 18_000, currency: INR } },
  ],
  billingUnitSeconds: 900,
};

/** The venue's console offer (ADR-0034): the second controller and on half price. */
export const FLOOR_CONSOLE_OFFERS: readonly ConsoleOffer[] = [
  {
    id: '01997f5e-0000-7000-8000-00000000c0f1',
    name: '2nd controller half price',
    active: true,
    priority: 0,
    stackable: false,
    maxDiscount: null,
    minControllers: 2,
    minDurationSeconds: null,
    stationClassIds: [],
    playersOnly: false,
    venueIds: [],
    daysOfWeek: [],
    startMinuteOfDay: null,
    endMinuteOfDay: null,
    validFrom: null,
    validUntil: null,
    reward: { kind: 'extra_controllers_percent', percentBps: 5_000 },
  },
];

/** A domain quote as the wire's `ConsoleQuote` (the api's `toConsoleQuoteDto`). */
function consoleQuoteDto(quote: ConsoleQuote): NonNullable<Session['booking']>['quote'] {
  return {
    lines: quote.lines.map((line) => ({
      kind: line.kind,
      blockIndex: line.blockIndex,
      blockMinutes: line.blockSeconds === null ? null : Math.round(line.blockSeconds / 60),
      fromSecond: line.fromSecond,
      seconds: line.seconds,
      quantity: line.quantity,
      unitAmount: line.unitAmount,
      amount: line.amount,
      basis: line.basis,
      discount: line.discount,
    })),
    offers: quote.applied.map((offer) => ({ promotionId: tid('prm', 'c0f'), name: offer.name, amount: offer.amount })),
    subtotal: quote.subtotal,
    discount: quote.discount,
    total: quote.total,
    chargedSeconds: quote.chargedSeconds,
    open: quote.open,
    peakControllers: quote.peakControllers,
    hourlyAmount: quote.hourlyAmount,
    billingUnitSeconds: FLOOR_CONSOLE_RATE.billingUnitSeconds,
  };
}

/** A console session's booking in the fake: the blocks, the controllers, and the order placed at the end. */
interface FakeBooking {
  blocks: { seconds: number | null; addedAt: string }[];
  controllers: { fromSecond: number; controllers: number; at: string }[];
  saleId: string | null;
}

/** What a booking comes to at `elapsed` seconds, `walkIn` deciding a players-only offer. */
function quoteBooking(booking: FakeBooking, elapsed: number, walkIn: boolean): ConsoleQuote {
  return quoteConsoleSession(
    FLOOR_CONSOLE_RATE,
    { blocks: booking.blocks.map((block) => ({ seconds: block.seconds })), controllers: booking.controllers, elapsedSeconds: elapsed },
    FLOOR_CONSOLE_OFFERS,
    { venueId: FLOOR_VENUE_ID, stationClassId: LOUNGE.id, hasPlayer: !walkIn, at: Date.parse(FLOOR_NOW), timezone: 'Asia/Kolkata' },
  );
}

/** The session's `booking` (and its clock) as the api would read it now. */
function withBooking(session: Session, booking: FakeBooking): Session {
  const elapsed = Math.max(0, secondsBetween(session.startedAt, session.endedAt ?? FLOOR_NOW));
  const quote = quoteBooking(booking, elapsed, session.walkIn === true);
  const open = booking.blocks.some((block) => block.seconds === null);
  const booked = open ? 0 : booking.blocks.reduce((total, block) => total + (block.seconds ?? 0), 0);
  const ended = session.status === 'ended';
  const left = open || ended ? 0 : Math.max(0, booked - elapsed);
  return {
    ...session,
    currentSource: null,
    timeLeftSeconds: left,
    fundedEndAt: open || ended ? null : plusSeconds(FLOOR_NOW, left),
    plan: [],
    booking: {
      controllers: booking.controllers.at(-1)?.controllers ?? 1,
      open,
      bookedSeconds: booked,
      blocks: booking.blocks.map((block) => ({ minutes: block.seconds === null ? null : Math.round(block.seconds / 60), addedAt: block.addedAt })),
      controllerChanges: booking.controllers.map((change) => ({ ...change })),
      quote: consoleQuoteDto(quote),
      saleId: booking.saleId,
    },
  };
}

// -----------------------------------------------------------------------------------------------
// Players and staff (named here for readers of the fixtures; the session read carries only the id)
// -----------------------------------------------------------------------------------------------

// The same players the Players fake serves, so a session's player resolves to a real profile — the
// floor's tiles and List view and the station modal look each one up by id (`getPlayer`), exactly as the live server.
export const FLOOR_PLAYERS = [
  { id: FAKE_PLAYER_IDS.aarav, name: 'Aarav Mehta' },
  { id: FAKE_PLAYER_IDS.diya, name: 'diyarao' },
  { id: FAKE_PLAYER_IDS.kabir, name: 'kabir_s' },
  { id: FAKE_PLAYER_IDS.ananya, name: 'Ananya I' },
  { id: FAKE_PLAYER_IDS.rohan, name: 'rohang' },
  { id: FAKE_PLAYER_IDS.ishaan, name: 'Ishaan Nair' },
  { id: FAKE_PLAYER_IDS.meera, name: 'Meera Krishnan' },
] as const;
const player = (index: number): string => (FLOOR_PLAYERS[index] as { id: string }).id;

const STAFF = { arjun: { id: tid('mem', 'j01'), name: 'Arjun Nair' }, sneha: { id: tid('mem', 'j02'), name: 'Sneha Joshi' } } as const;

// -----------------------------------------------------------------------------------------------
// Stations: configuration and live presence
// -----------------------------------------------------------------------------------------------

type Presence = Pick<FloorStationState, 'online' | 'mode' | 'lastSeenAt' | 'title'>;

interface StationSeed {
  readonly number: number;
  readonly name: string | null;
  readonly klass: StationClass;
  readonly zone: Zone | null;
  readonly presence: Presence;
  readonly disabledReason?: string;
  /** Staff-run (ADR-0033): no app, no presence — the tile follows the session. Managed unless it says. */
  readonly staffRun?: boolean;
}

const LIVE_AT = at('13:59');
const online = (mode: NonNullable<FloorStationState['mode']>, title: FloorStationState['title'] = null): Presence => ({ online: true, mode, lastSeenAt: LIVE_AT, title });

/**
 * What a station is running now (F-CAT-04): PC-01 plays Dota 2, started 23 minutes before the fake floor was
 * created (so the tile reads "Playing Dota 2 · 23 min" whatever the clock says); PC-02 has Chrome in front,
 * no library title ("Other: chrome"). Every other station runs nothing.
 */
export const FLOOR_PLAYING_GAME_ID = tid('gam', 'd02');
const playingDota = (): FloorStationState['title'] => ({
  titleId: FLOOR_PLAYING_GAME_ID,
  exe: 'dota2',
  name: 'Dota 2',
  since: new Date(Date.now() - 23 * 60_000).toISOString(),
});
const CHROME: FloorStationState['title'] = { titleId: null, exe: 'chrome' };

/** Numbers 1–8 Standard in the main hall, 9–13 Pro in the arena, 14–16 the PS5 lounge. */
const STATION_SEEDS: readonly StationSeed[] = [
  { number: 1, name: 'PC-01', klass: STANDARD, zone: MAIN_HALL, presence: online('in_session') },
  { number: 2, name: 'PC-02', klass: STANDARD, zone: MAIN_HALL, presence: online('in_session', CHROME) },
  { number: 3, name: 'PC-03', klass: STANDARD, zone: MAIN_HALL, presence: online('locked') },
  { number: 4, name: 'PC-04', klass: STANDARD, zone: MAIN_HALL, presence: online('locked') },
  { number: 5, name: 'PC-05', klass: STANDARD, zone: MAIN_HALL, presence: { online: false, mode: null, lastSeenAt: at('13:12'), title: null } },
  { number: 6, name: 'PC-06', klass: STANDARD, zone: MAIN_HALL, presence: online('session_locked') },
  { number: 7, name: 'PC-07', klass: STANDARD, zone: MAIN_HALL, presence: online('maintenance') },
  { number: 8, name: 'PC-08', klass: STANDARD, zone: MAIN_HALL, presence: online('locked'), disabledReason: 'Monitor flickers — a replacement is on order.' },
  { number: 9, name: 'Pro-01', klass: PRO, zone: ARENA, presence: online('in_session') },
  { number: 10, name: 'Pro-02', klass: PRO, zone: ARENA, presence: online('in_session') },
  { number: 11, name: 'Pro-03', klass: PRO, zone: ARENA, presence: online('updating') },
  { number: 12, name: 'Pro-04', klass: PRO, zone: ARENA, presence: online('locked') },
  { number: 13, name: 'Pro-05', klass: PRO, zone: ARENA, presence: online('pairing') },
  // The PS5 lounge is run by staff (ADR-0033): no app to pair, never offline, the clock is the server's.
  { number: 14, name: 'PS5-A', klass: LOUNGE, zone: MAIN_HALL, presence: { online: false, mode: null, lastSeenAt: null, title: null }, staffRun: true },
  { number: 15, name: 'PS5-B', klass: LOUNGE, zone: MAIN_HALL, presence: { online: false, mode: null, lastSeenAt: null, title: null }, staffRun: true },
  // Goes by its number alone, unplaced: a console the venue runs by hand.
  { number: 16, name: null, klass: LOUNGE, zone: null, presence: { online: false, mode: null, lastSeenAt: null, title: null }, staffRun: true },
];

export const floorStationId = (number: number): string => tid('stn', `5${two(number)}`);

/** The venue-library title the PS5 lounge's sessions pick (ADR-0033): EA SPORTS FC 26. */
export const FLOOR_FC26_ENTRY_ID = tid('lib', 'f26');

// -----------------------------------------------------------------------------------------------
// Sessions and their timelines
// -----------------------------------------------------------------------------------------------

interface SessionFixture {
  session: Session;
  segments: SessionSegment[];
  events: SessionEvent[];
  /** A booked console session (ADR-0034): its blocks and controllers. */
  booking?: FakeBooking;
  /** F-SES-21: the titles played, the offline gaps and the journal entries the timeline also carries. */
  extras?: Pick<SessionTimeline, 'plays' | 'offline' | 'ledger'>;
  /** F-SES-21 rule 6: the session's journal entries as `…/ledger` pages them, oldest first here. */
  ledger?: SessionLedgerEntry[];
}

/**
 * PC-01's journal, a minute at a time as the api posts it: wallet play 12:40 to 13:19 at ₹60/h (₹1.00 a minute),
 * fifteen minutes of free time added at 13:18 and played 13:20 to 13:34, then the time sold on account from 13:35,
 * with the four minutes charged past the station-reported end given back at 13:44. 82 entries: two pages of 50.
 */
function pcOneLedger(): SessionLedgerEntry[] {
  const entries: SessionLedgerEntry[] = [];
  let serial = 0;
  const minute = (base: string, plus: number): string => plusSeconds(at(base), plus * 60);
  const push = (entry: Omit<SessionLedgerEntry, 'id'>): void => {
    serial += 1;
    entries.push({ id: tid('jen', `k${two(serial)}`), ...entry });
  };
  for (let m = 0; m < 40; m += 1) push({ kind: 'segment_charge', at: minute('12:40', m), memo: null, reversal: false, amount: money(100), seconds: 0, funding: 'wallet' });
  push({ kind: 'comp_grant', at: at('13:18'), memo: null, reversal: false, amount: money(0), seconds: 0, funding: 'comp' });
  for (let m = 0; m < 15; m += 1) push({ kind: 'segment_charge', at: minute('13:20', m), memo: null, reversal: false, amount: money(0), seconds: 60, funding: 'comp' });
  for (let m = 0; m < 25; m += 1) {
    push({ kind: 'segment_charge', at: minute('13:35', m), memo: null, reversal: false, amount: money(100), seconds: 0, funding: 'postpaid' });
    if (m === 9) {
      push({ kind: 'segment_charge_reversal', at: minute('13:35', m), memo: 'Play charged past the station-reported end', reversal: true, amount: money(-400), seconds: 0, funding: 'postpaid' });
    }
  }
  return entries;
}

let eventSerial = 0;
const event = (kind: SessionEvent['kind'], when: string, actorType: SessionEvent['actorType'], actorId: string | null, detail: Record<string, unknown> = {}): SessionEvent => {
  eventSerial += 1;
  return { id: tid('sev', `7${two(eventSerial)}`), kind, at: when, actorType, actorId, detail };
};

let segmentSerial = 0;
/** A billed segment; `happyHour` is a play promotion's cut (F-POS-03): `chargeAmount` is already net of it. */
const segment = (
  stationId: string,
  source: SessionSegment['source'],
  from: string,
  to: string | null,
  amount: number,
  ledger: boolean,
  happyHour?: { readonly percentBps: number; readonly amount: number },
): SessionSegment => {
  segmentSerial += 1;
  return {
    id: tid('ssg', `8${two(segmentSerial)}`),
    stationId,
    source,
    startedAt: from,
    endedAt: to,
    chargedSeconds: secondsBetween(from, to ?? FLOOR_NOW),
    chargeAmount: money(amount),
    discount: money(happyHour?.amount ?? 0),
    promotions: happyHour === undefined ? [] : [{ promotionId: tid('prm', `9${two(segmentSerial)}`), name: 'Happy hour', percentBps: happyHour.percentBps, amount: money(happyHour.amount) }],
    ledgerEntryId: ledger ? tid('jen', `g${two(segmentSerial)}`) : null,
  };
};

function sessionOn(
  number: number,
  playerIndex: number,
  fields: Pick<Session, 'status' | 'startedAt' | 'timeLeftSeconds' | 'currentSource' | 'plan'> & {
    fundedEndAt?: string | null;
    chargedSeconds: number;
    /** The funding breakdown (spec/05 §4.4), when the fixture carries one. */
    funding?: Session['funding'];
    /** A staff-run session (ADR-0033): no lease, the server's clock, the title staff picked. */
    staffRun?: { readonly title: string | null };
  },
  segments: SessionSegment[],
  events: SessionEvent[],
): SessionFixture {
  const chargedAmount = segments.reduce((sum, item) => sum + item.chargeAmount.amount, 0);
  return {
    session: {
      id: tid('ses', `6${two(number)}`),
      organizationId: FLOOR_ORGANIZATION_ID,
      venueId: FLOOR_VENUE_ID,
      stationId: floorStationId(number),
      playerId: player(playerIndex),
      status: fields.status,
      startedAt: fields.startedAt,
      endedAt: null,
      endReason: null,
      currentSource: fields.currentSource,
      timeLeftSeconds: fields.timeLeftSeconds,
      fundedEndAt: fields.fundedEndAt === undefined ? plusSeconds(FLOOR_NOW, fields.timeLeftSeconds) : fields.fundedEndAt,
      plan: fields.plan,
      charged: { chargedSeconds: fields.chargedSeconds, chargedAmount: money(chargedAmount) },
      serverTime: FLOOR_NOW,
      ...(fields.funding === undefined ? {} : { funding: fields.funding }),
      graceSeconds: 300,
      ...(fields.staffRun === undefined
        ? {}
        : {
            staffRun: true,
            ...(fields.staffRun.title === null ? {} : { staffTitle: { libraryEntryId: FLOOR_FC26_ENTRY_ID, name: fields.staffRun.title } }),
          }),
    },
    segments,
    events,
  };
}

function seedSessions(): SessionFixture[] {
  const fixtures = seedSessionList();
  const first = fixtures[0];
  if (first !== undefined) {
    first.extras = {
      plays: [
        { id: tid('gpl', '101'), name: 'Valorant', detectedBy: 'launch', startedAt: at('12:42'), endedAt: at('13:15'), seconds: 1980 },
        { id: tid('gpl', '102'), name: 'Counter-Strike 2', detectedBy: 'catalogue', startedAt: at('13:36'), endedAt: at('13:49'), seconds: 780 },
      ],
      offline: [
        {
          kind: 'end_report',
          from: at('13:40'),
          at: at('13:44'),
          measuredSeconds: 240,
          overrunSeconds: 0,
          graceCreditSeconds: 0,
          chargedSeconds: 0,
          chargedAmount: null,
          codeAction: null,
          policy: 'continue_and_settle',
        },
      ],
      ledger: [
        { id: tid('jen', 'e01'), kind: 'segment_charge', at: at('13:40'), memo: null, reversal: false, amount: money(2500), seconds: 0 },
        { id: tid('jen', 'e02'), kind: 'segment_charge_reversal', at: at('13:44'), memo: 'Play charged past the station-reported end', reversal: true, amount: money(-400), seconds: 0 },
      ],
    };
    first.ledger = pcOneLedger();
  }
  return fixtures;
}

function seedSessionList(): SessionFixture[] {
  eventSerial = 0;
  segmentSerial = 0;
  const s = floorStationId;
  return [
    // PC-01 — Aarav Mehta, 1:20 left, with the fullest history: wallet play, a comp for a headset swap, the player
    // turning "Use my wallet for play time" off at the kiosk (F-WAL-04) — the wallet stops at 13:20 — time sold at the
    // counter, a message, a step-away lock and unlock, and the wallet turned back on, so it follows the sold time.
    sessionOn(
      1,
      0,
      {
        status: 'active',
        startedAt: at('12:40'),
        timeLeftSeconds: 4800,
        currentSource: 'postpaid',
        plan: [
          { source: 'postpaid', label: 'Sold at the counter', seconds: 1800 },
          { source: 'wallet', label: null, seconds: 3000 },
        ],
        chargedSeconds: 900,
        // What the 1:20 is made of, in the order it is used (spec/05 §4.4): 30 minutes sold on account, then 50 from
        // the wallet at ₹60 an hour, then five minutes of grace.
        funding: {
          items: [
            { kind: 'postpaid', seconds: 1800, name: null, holdingId: null, expiresAt: null, windowClosesAt: null, hourlyRate: money(6000), billingUnitSeconds: 60, amount: money(3000) },
            { kind: 'wallet', seconds: 3000, name: null, holdingId: null, expiresAt: null, windowClosesAt: null, hourlyRate: money(6000), billingUnitSeconds: 60, amount: money(5000) },
          ],
          totalSeconds: 4800,
          then: { kind: 'grace', seconds: 300 },
        },
      },
      [segment(s(1), 'wallet', at('12:40'), at('13:20'), 4000, true), segment(s(1), 'comp', at('13:20'), at('13:35'), 0, false), segment(s(1), 'postpaid', at('13:35'), null, 2500, true)],
      [
        event('started', at('12:40'), 'player', player(0), { funding: 'wallet' }),
        event('comped', at('13:18'), 'member', STAFF.arjun.id, { minutes: 15, reason: 'other', note: 'Headset swap' }),
        event('wallet_disabled', at('13:19'), 'player', player(0), { via: 'kiosk', walletFundedSeconds: 0 }),
        event('sold_time', at('13:34'), 'member', STAFF.sneha.id, { minutes: 30, amount: money(3000) }),
        event('message_sent', at('13:45'), 'member', STAFF.sneha.id, { text: 'Your paneer tikka roll is ready at the counter.', needsAck: false }),
        event('locked', at('13:50'), 'device', floorStationId(1), { reason: 'step_away' }),
        event('unlocked', at('13:54'), 'player', player(0)),
        event('wallet_enabled', at('13:55'), 'player', player(0), { via: 'kiosk', walletFundedSeconds: 3000 }),
      ],
    ),
    // …and what else happened on PC-01 (F-SES-21): two titles, a short offline gap, and a charge corrected by it.
    // (Attached to the fixture below, after the list is built.)
    // PC-02 — diyarao, ending soon: six minutes of wallet money left, the ten-minute warning sent.
    sessionOn(
      2,
      1,
      { status: 'active', startedAt: at('12:56'), timeLeftSeconds: 360, currentSource: 'wallet', plan: [{ source: 'wallet', label: null, seconds: 360 }], chargedSeconds: 0 },
      [segment(s(2), 'wallet', at('12:56'), null, 6400, true, { percentBps: 2000, amount: 1600 })],
      [event('started', at('12:56'), 'player', player(1), { funding: 'wallet' }), event('warned', at('13:56'), 'system', null, { minutes: 10 })],
    ),
    // PC-06 — kabir_s, on a pass, stepped away: the screen is locked.
    sessionOn(
      6,
      2,
      { status: 'locked', startedAt: at('13:10'), timeLeftSeconds: 2400, currentSource: 'pass', plan: [{ source: 'pass', label: 'Evening 2-hour pass', seconds: 2400 }], chargedSeconds: 3000 },
      [segment(s(6), 'pass', at('13:10'), null, 0, false)],
      [event('started', at('13:10'), 'player', player(2), { funding: 'pass' }), event('locked', at('13:57'), 'device', floorStationId(6), { reason: 'step_away' })],
    ),
    // Pro-01 — Ananya I, overdue: two wallet hours ran out at 13:52 and grace is running.
    sessionOn(
      9,
      3,
      {
        status: 'grace',
        startedAt: at('11:52'),
        timeLeftSeconds: 0,
        fundedEndAt: at('13:52'),
        currentSource: 'grace',
        plan: [{ source: 'grace', label: null, seconds: 120 }],
        chargedSeconds: 0,
      },
      [segment(s(9), 'wallet', at('11:52'), at('13:52'), 24000, true)],
      [
        event('started', at('11:52'), 'player', player(3), { funding: 'wallet' }),
        event('warned', at('13:42'), 'system', null, { minutes: 10 }),
        event('warned', at('13:47'), 'system', null, { minutes: 5 }),
        event('grace_started', at('13:52'), 'system', null),
      ],
    ),
    // Pro-02 — rohang, 2:30 left on a Pro pass.
    sessionOn(
      10,
      4,
      { status: 'active', startedAt: at('13:30'), timeLeftSeconds: 9000, currentSource: 'pass', plan: [{ source: 'pass', label: 'Pro 3-hour pass', seconds: 9000 }], chargedSeconds: 1800 },
      [segment(s(10), 'pass', at('13:30'), null, 0, false)],
      [event('started', at('13:30'), 'player', player(4), { funding: 'pass' })],
    ),
    // PS5-A — Aditya Kulkarni and a friend (ADR-0034): booked an hour for two controllers at 13:15, then 30 minutes
    // more at 13:50 — 45 minutes left, paid once at the counter when it ends.
    bookedOn(
      sessionOn(
        14,
        5,
        { status: 'active', startedAt: at('13:15'), timeLeftSeconds: 2700, currentSource: null, plan: [], chargedSeconds: 0, staffRun: { title: 'EA SPORTS FC 26' } },
        [],
        [
          event('started', at('13:15'), 'member', STAFF.arjun.id, { controllers: 2, minutes: 60 }),
          event('time_added', at('13:50'), 'member', STAFF.arjun.id, { minutes: 30 }),
        ],
      ),
      { blocks: [{ seconds: 3600, addedAt: at('13:15') }, { seconds: 1800, addedAt: at('13:50') }], controllers: [{ fromSecond: 0, controllers: 2, at: at('13:15') }], saleId: null },
    ),
  ];
}

/** A staff-run fixture made a booked console session (ADR-0034). */
function bookedOn(fixture: SessionFixture, booking: FakeBooking): SessionFixture {
  return { ...fixture, booking, session: withBooking(fixture.session, booking) };
}

// -----------------------------------------------------------------------------------------------
// Calls, kitchen tickets and the day's takings
// -----------------------------------------------------------------------------------------------

function seedCalls(sessions: readonly SessionFixture[]): StationCall[] {
  const sessionOnStation = (number: number) => sessions.find((item) => item.session.stationId === floorStationId(number))?.session.id ?? null;
  // Newest first, as the api lists them.
  return [
    {
      id: tid('cll', '901'),
      stationId: floorStationId(2),
      sessionId: sessionOnStation(2),
      playerId: player(1),
      category: 'peripherals',
      note: 'Left earcup of the headset is crackling',
      state: 'open',
      raisedAt: at('13:57'),
      acknowledgedAt: null,
      acknowledgedByName: null,
      closedAt: null,
    },
    {
      id: tid('cll', '902'),
      stationId: floorStationId(9),
      sessionId: sessionOnStation(9),
      playerId: player(3),
      category: 'food',
      note: 'Two masala chai and a veg puff, please',
      state: 'acknowledged',
      raisedAt: at('13:51'),
      acknowledgedAt: at('13:53'),
      acknowledgedByName: STAFF.arjun.name,
      closedAt: null,
    },
    {
      id: tid('cll', '903'),
      stationId: floorStationId(4),
      sessionId: null,
      playerId: player(6),
      category: 'pc',
      note: 'Mouse keeps double-clicking',
      state: 'resolved',
      raisedAt: at('13:20'),
      acknowledgedAt: at('13:21'),
      acknowledgedByName: STAFF.sneha.name,
      closedAt: at('13:28'),
    },
  ];
}

const KITCHEN_TICKETS: readonly KitchenTicket[] = [
  {
    id: tid('ktk', 'd01'),
    orderId: tid('fod', 'f01'),
    saleId: null,
    orderNumber: null,
    paid: false,
    venueId: FLOOR_VENUE_ID,
    area: 'kitchen',
    state: 'preparing',
    stationId: floorStationId(1),
    note: null,
    items: [{ id: tid('kti', 'e01'), name: 'Paneer tikka roll', quantity: 1, modifiers: ['Extra mint chutney'], note: null, sortOrder: 0 }],
    placedAt: at('13:40'),
    acceptedAt: at('13:42'),
    preparingAt: null,
    readyAt: null,
    deliveredAt: null,
    updatedAt: at('13:42'),
  },
  {
    id: tid('ktk', 'd02'),
    orderId: tid('fod', 'f02'),
    saleId: null,
    orderNumber: null,
    paid: false,
    venueId: FLOOR_VENUE_ID,
    area: 'bar',
    state: 'new',
    stationId: floorStationId(9),
    note: 'Less sugar in one chai',
    items: [
      { id: tid('kti', 'e02'), name: 'Masala chai', quantity: 2, modifiers: [], note: null, sortOrder: 0 },
      { id: tid('kti', 'e03'), name: 'Veg puff', quantity: 1, modifiers: [], note: null, sortOrder: 1 },
    ],
    placedAt: at('13:54'),
    acceptedAt: null,
    preparingAt: null,
    readyAt: null,
    deliveredAt: null,
    updatedAt: at('13:54'),
  },
  {
    id: tid('ktk', 'd03'),
    orderId: tid('fod', 'f03'),
    saleId: null,
    orderNumber: null,
    paid: false,
    venueId: FLOOR_VENUE_ID,
    area: 'bar',
    state: 'ready',
    stationId: null,
    note: null,
    items: [{ id: tid('kti', 'e04'), name: 'Cold coffee', quantity: 2, modifiers: ['Oat milk'], note: null, sortOrder: 0 }],
    placedAt: at('13:48'),
    acceptedAt: at('13:49'),
    preparingAt: null,
    readyAt: at('13:58'),
    deliveredAt: null,
    updatedAt: at('13:58'),
  },
  {
    id: tid('ktk', 'd04'),
    orderId: tid('fod', 'f04'),
    saleId: null,
    orderNumber: null,
    paid: false,
    venueId: FLOOR_VENUE_ID,
    area: 'kitchen',
    state: 'delivered',
    stationId: floorStationId(14),
    note: null,
    items: [{ id: tid('kti', 'e05'), name: 'Peri-peri fries', quantity: 1, modifiers: [], note: null, sortOrder: 0 }],
    placedAt: at('13:05'),
    acceptedAt: at('13:06'),
    preparingAt: null,
    readyAt: at('13:14'),
    deliveredAt: at('13:16'),
    updatedAt: at('13:16'),
  },
];
const ACTIVE_TICKET_STATES: ReadonlySet<KitchenTicket['state']> = new Set(['new', 'accepted', 'preparing', 'ready']);

const DAY_CLOSE: DayCloseReview = {
  venueId: FLOOR_VENUE_ID,
  businessDay: '2026-09-26',
  closed: false,
  allShiftsClosed: false,
  openShiftCount: 1,
  tenders: [
    { tender: 'cash', amount: money(184500) },
    { tender: 'counter_upi', amount: money(326000) },
    { tender: 'counter_card', amount: money(92000) },
  ],
  categories: [
    { category: 'play', amount: money(420000) },
    { category: 'food', amount: money(182500) },
    { category: 'retail', amount: money(0) },
    { category: 'other', amount: money(0) },
  ],
  refunds: { count: 0, amount: money(0) },
  voids: { count: 0, amount: money(0) },
  discounts: money(0),
  comps: { sessions: 0, seconds: 0 },
  receivablesRaised: money(0),
  passSales: money(420000),
  walletTopUps: money(0),
  walletCreditGiven: money(0),
  totalCashVariance: money(-500),
  // M-01 for the day (F-ANL-01): the payments net of refunds; no wallet top-up in this day, so it equals the tenders.
  revenue: money(602500),
  revenueBridge: {
    sales: money(602500),
    discounts: money(0),
    tax: money(0),
    refunds: money(0),
    onAccount: money(0),
    walletBonus: money(0),
    walletSettling: money(0),
    taxPaidAtTopUp: money(0),
    other: money(0),
    revenue: money(602500),
  },
  // Money in (2026-10-02): the cash, UPI and card taken; nothing was spent from wallets.
  moneyIn: money(602500),
  walletSpent: money(0),
  bonusSpent: money(0),
  shifts: [
    {
      id: tid('shf', 'h01'),
      status: 'closed',
      counterName: 'Front counter',
      openedByMemberId: STAFF.sneha.id,
      openedByName: null,
      openedAt: '2026-09-26T04:30:00.000Z',
      closedByMemberId: STAFF.sneha.id,
      closedAt: '2026-09-26T10:30:00.000Z',
      expectedCash: money(185000),
      countedCash: money(184500),
      cashVariance: money(-500),
      varianceAlerted: false,
    },
    {
      id: tid('shf', 'h02'),
      status: 'open',
      counterName: 'Front counter',
      openedByMemberId: STAFF.arjun.id,
      openedByName: null,
      openedAt: '2026-09-26T10:30:00.000Z',
      closedByMemberId: null,
      closedAt: null,
      expectedCash: null,
      countedCash: null,
      cashVariance: null,
      varianceAlerted: false,
    },
  ],
  closedByMemberId: null,
  closedAt: null,
  closeMode: null,
  signedOffByMemberId: null,
  signedOffAt: null,
};

// -----------------------------------------------------------------------------------------------
// Station health (F-STN-11, F-STN-15): PC-04's disk is nearly full, and a restart sent to the offline PC-05 expired
// -----------------------------------------------------------------------------------------------

const STATION_ALERTS: readonly StationAlert[] = [
  {
    id: tid('sal', 'a01'),
    stationId: floorStationId(4),
    kind: 'disk_low',
    severity: 'warning',
    detail: '8 GB free on C:',
    openedAt: at('13:40'),
    clearedAt: null,
    clearRule: 'condition',
  },
];

const CLEARED_ALERTS: readonly StationAlert[] = [
  {
    id: tid('sal', 'a02'),
    stationId: floorStationId(4),
    kind: 'agent_restarted',
    severity: 'warning',
    detail: '4 restarts in 10 minutes',
    openedAt: at('12:05'),
    clearedAt: at('12:20'),
    clearRule: 'quiet_period',
    clearReason: 'resolved',
  },
];

const NEEDS_ATTENTION_COMMANDS: NeedsAttention['commands'] = [
    {
      id: tid('cmd', 'e01'),
      stationId: floorStationId(5),
      type: 'station.power',
      status: 'expired',
      errorCode: null,
      errorMessage: null,
      createdAt: at('13:10'),
      completedAt: at('13:11'),
    },
];

const sample = (hhmm: string, diskFreeGb: number, gpuTempC: number | null): StationTelemetrySample => ({
  at: at(hhmm),
  mode: 'locked',
  cpuPct: 4,
  ramPct: 31,
  gpuTempC,
  diskFreeGb,
  uptimeSeconds: 7200,
  idleSeconds: 600,
  foregroundExe: null,
});

const TELEMETRY: readonly StationTelemetrySample[] = [sample('13:30', 11, 52), sample('13:40', 8, 51), sample('13:50', 8, 50)];

/** A station's badge from the open alerts it has now (a snoozed one wears none). */
function healthOf(alerts: readonly StationAlert[], stationId: string): FloorStationHealth {
  const open = alerts.filter((alert) => alert.stationId === stationId && alert.snoozedUntil === undefined);
  if (open.length === 0) return { openAlerts: 0, severity: null, kinds: [] };
  return {
    openAlerts: open.length,
    severity: open.some((alert) => alert.severity === 'critical') ? 'critical' : 'warning',
    kinds: open.map((alert) => alert.kind),
  };
}

// -----------------------------------------------------------------------------------------------
// The handler
// -----------------------------------------------------------------------------------------------

const ORGANIZATION_PATH = `/v1/organizations/${FLOOR_ORGANIZATION_ID}`;
const VENUE_PATH = `${ORGANIZATION_PATH}/venues/${FLOOR_VENUE_ID}`;

/** The paths this module answers, for the lead's routing and for the UI-steps note. */
export const FLOOR_PATHS = {
  floor: new RegExp(`^${VENUE_PATH}/floor$`),
  stations: new RegExp(`^${VENUE_PATH}/stations$`),
  stationClasses: new RegExp(`^${VENUE_PATH}/station-classes$`),
  zones: new RegExp(`^${VENUE_PATH}/zones$`),
  stationSession: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/session$`),
  session: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)$`),
  sessionTimeline: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)/timeline$`),
  sessionLedger: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)/ledger$`),
  sessionAction: new RegExp(`^${ORGANIZATION_PATH}/sessions/(ses_[0-9a-z]+)/(comp|sell-time|message|end|lock|add-time|controllers|quote)$`),
  /** ADR-0033: staff start a session (the Floor's Start session), and a staff-run station's countdown-screen link. */
  startSession: new RegExp(`^${VENUE_PATH}/sessions$`),
  /** ADR-0034: the start form's price preview for a console session. */
  consoleQuote: new RegExp(`^${VENUE_PATH}/sessions/quote$`),
  stationDisplay: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/display$`),
  calls: new RegExp(`^${VENUE_PATH}/calls$`),
  callAction: new RegExp(`^${VENUE_PATH}/calls/(cll_[0-9a-z]+)/(acknowledge|resolve)$`),
  kitchenTickets: new RegExp(`^${VENUE_PATH}/kitchen/tickets$`),
  dayClose: new RegExp(`^${VENUE_PATH}/day-close$`),
  floorLive: new RegExp(`^${VENUE_PATH}/floor/live$`),
  stationHealth: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/health$`),
  stationTelemetry: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/telemetry$`),
  stationAlertsClear: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/alerts/clear$`),
  stationChange: new RegExp(`^${VENUE_PATH}/stations/(stn_[0-9a-z]+)/(unpair|remove)$`),
  needsAttention: new RegExp(`^${VENUE_PATH}/needs-attention$`),
} as const;

export function createFakeFloor(helpers: FloorHelpers) {
  const { json, problem, gate } = helpers;
  /** ADR-0034: a booked console session that ends places one order (the commerce fake's awaiting queue). */
  const onConsoleOrder = helpers.onConsoleOrder;
  const can = helpers.can ?? (() => true);
  const sessions = seedSessions();
  const state = {
    sessions,
    calls: seedCalls(sessions),
    /** Live presence by station id; ending a session puts its station back to `locked` (free). */
    presence: new Map(
      STATION_SEEDS.map((seed) => [floorStationId(seed.number), seed.number === 1 ? { ...seed.presence, title: playingDota() } : { ...seed.presence }]),
    ),
    /** Station health (F-STN-11): the open alerts, and the cleared ones — clearing by hand moves one across. */
    openAlerts: [...STATION_ALERTS] as StationAlert[],
    clearedAlerts: [...CLEARED_ALERTS] as StationAlert[],
    /** ADR-0033: the countdown-screen links by station — the token (the fake keeps it to answer the public read). */
    displays: new Map<string, { id: string; token: string; createdAt: string; lastSeenAt: string | null }>(),
    displaySerial: 0,
    startSerial: 0,
    /** ADR-0057: stations taken out of the venue, and PCs unpaired from a station (back at their pairing screen). */
    removed: new Set<string>(),
    unpaired: new Set<string>(),
    /** Stations made from new PCs (ADR-0065), and the client each runs. */
    made: [] as StationSeed[],
  };
  const clientOf = (seed: StationSeed): 'station' | 'none' => (state.made.includes(seed) ? (helpers.madeClient?.() ?? 'station') : (helpers.clientOf?.(seed.number) ?? 'station'));
  // A Boot-only venue's floor is PCs only (ADR-0065): a console never starts from the network, so the lounge is left out.
  const seedsNow = () => [...STATION_SEEDS.filter((seed) => helpers.pcsOnly !== true || seed.klass.id !== LOUNGE.id), ...state.made].filter((seed) => !state.removed.has(floorStationId(seed.number)));

  const runningOn = (stationId: string) => state.sessions.find((item) => item.session.stationId === stationId && item.session.status !== 'ended');

  const floorStation = (seed: StationSeed): FloorStationState => {
    const id = floorStationId(seed.number);
    const base = {
      stationId: id,
      number: seed.number,
      name: seed.name,
      kind: seed.klass.id === LOUNGE.id ? ('console' as const) : ('pc' as const),
      classId: seed.klass.id,
      zoneId: seed.zone?.id ?? null,
      capacity: seed.klass.defaultCapacity,
      status: seed.disabledReason === undefined ? ('active' as const) : ('disabled' as const),
      disabledReason: seed.disabledReason ?? null,
    };
    // No Clavian Station app (ADR-0065): nothing reports presence; the tile says how the PC starts.
    if (clientOf(seed) === 'none') {
      return { ...base, managed: true, client: 'none', online: false, mode: null, lastSeenAt: null, title: null, health: { openAlerts: 0, severity: null, kinds: [] } };
    }
    // A staff-run station (ADR-0033): never online, never offline — its mode is its session's.
    if (seed.staffRun === true) {
      const running = runningOn(id);
      const mode = running === undefined ? 'locked' : running.session.status === 'locked' ? 'session_locked' : 'in_session';
      return { ...base, managed: false, online: false, mode, lastSeenAt: null, title: null, health: { openAlerts: 0, severity: null, kinds: [] } };
    }
    const presence = state.unpaired.has(id) ? { online: false, mode: 'pairing' as const, lastSeenAt: null, title: null } : (state.presence.get(id) ?? seed.presence);
    return { ...base, managed: true, ...presence, ...(state.made.includes(seed) ? { paired: false } : {}), health: healthOf(state.openAlerts, id) };
  };

  const station = (seed: StationSeed): Station => {
    const live = floorStation(seed);
    const paired = live.mode !== 'pairing' && live.lastSeenAt !== null;
    return {
      id: live.stationId,
      organizationId: FLOOR_ORGANIZATION_ID,
      venueId: FLOOR_VENUE_ID,
      number: live.number,
      name: live.name,
      kind: live.kind,
      role: 'station',
      managed: live.managed,
      client: clientOf(seed),
      classId: live.classId,
      zoneId: live.zoneId,
      capacity: live.capacity,
      status: live.status,
      disabledReason: live.disabledReason,
      // The list's `mode` is never null: an offline station keeps its last mode, `pairing` until paired.
      mode: seed.staffRun === true ? 'locked' : (live.mode ?? (paired ? 'locked' : 'pairing')),
      deviceId: paired && seed.staffRun !== true ? tid('dev', `k${two(seed.number)}`) : null,
      createdAt: CREATED,
      updatedAt: CREATED,
    };
  };

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      detail: 'The request could not be understood as written.',
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });

  /** A collection page, with an offset cursor. */
  const paged = <Row>(rows: readonly Row[], query: URLSearchParams): FakeResponse => {
    // Unknown parameters are refused, as the api does (spec/08 §2.4).
    const parsed = PageQuerySchema.strict().safeParse(Object.fromEntries(query));
    if (!parsed.success) return invalid(parsed.error);
    const start = parsed.data.cursor === undefined ? 0 : Number(parsed.data.cursor);
    const page = rows.slice(start, start + parsed.data.limit);
    const next = start + parsed.data.limit < rows.length ? String(start + parsed.data.limit) : null;
    return json(200, { data: page, page: { cursor: next, hasMore: next !== null, count: page.length } });
  };

  const live = (fixture: SessionFixture) => fixture.session.status !== 'ended';
  const findSession = (id: string | undefined) => state.sessions.find((item) => item.session.id === id);
  const notFound = (title: string) => problem(404, 'not_found', title);

  /** Add funded seconds to a running session, as comp (free) or sold time (charged at the class rate). */
  function addTime(fixture: SessionFixture, minutes: number, source: 'comp' | 'postpaid'): void {
    const current = fixture.session;
    const seconds = minutes * 60;
    const timeLeftSeconds = (current.status === 'grace' ? 0 : current.timeLeftSeconds) + seconds;
    const seed = STATION_SEEDS.find((item) => floorStationId(item.number) === current.stationId);
    const amount = source === 'comp' ? 0 : minutes * (RATE_PER_MINUTE[seed?.klass.id ?? ''] ?? 0);
    fixture.session = {
      ...current,
      status: current.status === 'grace' ? 'active' : current.status,
      currentSource: current.status === 'grace' ? source : current.currentSource,
      timeLeftSeconds,
      fundedEndAt: plusSeconds(FLOOR_NOW, timeLeftSeconds),
      plan: [...current.plan.filter((item) => item.source !== 'grace'), { source, label: null, seconds }],
      charged: {
        chargedSeconds: current.charged.chargedSeconds,
        // Sold time is charged up front on account here: the segment and the session move together.
        chargedAmount: money(current.charged.chargedAmount.amount + amount),
      },
    };
    if (amount > 0) fixture.segments.push(segment(current.stationId, 'postpaid', FLOOR_NOW, null, amount, true));
  }

  /** What a countdown screen shows (ADR-0033): the station, the venue, a first name and the clock — nothing more. */
  function displayView(stationId: string): Record<string, unknown> | undefined {
    const seed = STATION_SEEDS.find((item) => floorStationId(item.number) === stationId);
    if (seed === undefined) return undefined;
    const running = runningOn(stationId);
    const player = running === undefined ? undefined : FLOOR_PLAYERS.find((item) => item.id === running.session.playerId);
    const session = running?.session;
    return {
      station: { number: seed.number, name: seed.name, kind: seed.klass.id === LOUNGE.id ? 'console' : 'pc' },
      venue: { name: 'Playground Central', logoUrl: null },
      session:
        session === undefined
          ? null
          : {
              status: session.status === 'grace' ? 'grace' : session.status === 'locked' ? 'locked' : 'active',
              playerLabel: player === undefined ? null : (player.name.split(/\s+/)[0] ?? null),
              timeLeftSeconds: session.status === 'grace' ? 0 : session.timeLeftSeconds,
              fundedEndAt: session.fundedEndAt,
              graceSeconds: session.graceSeconds ?? 0,
              ...(session.graceEndsAt === undefined ? {} : { graceEndsAt: session.graceEndsAt }),
              title: session.staffTitle?.name ?? null,
            },
      playerAppUrl: 'https://play.clavian.io',
      serverTime: FLOOR_NOW,
    };
  }

  function handle(method: string, pathname: string, query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    // The countdown screen's public read (ADR-0033): no session — the token is the credential.
    const publicDisplay = /^\/v1\/public\/displays\/([^/]+)(\/realtime-token)?$/.exec(pathname);
    if (publicDisplay !== null) {
      const entry = [...state.displays.entries()].find(([, link]) => link.token === publicDisplay[1]);
      if (entry === undefined) return problem(404, 'not_found', 'No live countdown screen has that token.');
      // No realtime in the fake: the screen falls back to polling, as it does when the socket is down.
      if (publicDisplay[2] !== undefined) return problem(500, 'internal_error', 'Realtime is not configured here.');
      entry[1].lastSeenAt = FLOOR_NOW;
      const view = displayView(entry[0]);
      return view === undefined ? problem(404, 'not_found', 'No live countdown screen has that token.') : json(200, view);
    }
    if (!pathname.startsWith(`${ORGANIZATION_PATH}/`)) return undefined;
    const matched = Object.values(FLOOR_PATHS).some((pattern) => pattern.test(pathname));
    if (!matched) return undefined;
    const refused = gate();
    if (refused !== undefined) return refused;

    // -- Reads ---------------------------------------------------------------------------------
    if (method === 'GET') {
      if (FLOOR_PATHS.floor.test(pathname)) return json(200, { version: 42, stations: seedsNow().map(floorStation) });
      if (FLOOR_PATHS.stations.test(pathname)) return paged(seedsNow().map(station), query);
      if (FLOOR_PATHS.stationClasses.test(pathname)) return paged(FLOOR_CLASSES, query);
      if (FLOOR_PATHS.zones.test(pathname)) return paged(FLOOR_ZONES, query);

      const onStation = FLOOR_PATHS.stationSession.exec(pathname);
      if (onStation !== null) {
        const fixture = state.sessions.find((item) => item.session.stationId === onStation[1] && live(item));
        return fixture === undefined ? notFound('No such station, or it has no active session.') : json(200, fixture.session);
      }
      const timeline = FLOOR_PATHS.sessionTimeline.exec(pathname);
      if (timeline !== null) {
        const fixture = findSession(timeline[1]);
        return fixture === undefined
          ? notFound('No such session in this organisation.')
          : json(200, { sessionId: fixture.session.id, segments: fixture.segments, events: fixture.events, ...(fixture.extras ?? {}) });
      }
      // F-SES-21 rule 6: the journal entries, newest first, a page at a time.
      const ledger = FLOOR_PATHS.sessionLedger.exec(pathname);
      if (ledger !== null) {
        const fixture = findSession(ledger[1]);
        return fixture === undefined ? notFound('No such session in this organisation.') : paged([...(fixture.ledger ?? [])].reverse(), query);
      }
      const one = FLOOR_PATHS.session.exec(pathname);
      if (one !== null) {
        const fixture = findSession(one[1]);
        return fixture === undefined ? notFound('No such session in this organisation.') : json(200, fixture.session);
      }

      if (FLOOR_PATHS.calls.test(pathname)) return json(200, { calls: state.calls });
      if (FLOOR_PATHS.kitchenTickets.test(pathname)) {
        const wanted = query.get('state');
        const area = query.get('area');
        const tickets = KITCHEN_TICKETS.filter((ticket) => (wanted === null ? ACTIVE_TICKET_STATES.has(ticket.state) : ticket.state === wanted)).filter(
          (ticket) => area === null || ticket.area === area,
        );
        return json(200, { tickets });
      }
      if (FLOOR_PATHS.dayClose.test(pathname)) return json(200, DAY_CLOSE);
      // The floor's live figures (F-ANL-02): takings today by M-01 — the day close's revenue — and the counts.
      if (FLOOR_PATHS.floorLive.test(pathname)) {
        const running = state.sessions.filter(live).length;
        return json(200, {
          venueId: FLOOR_VENUE_ID,
          businessDay: DAY_CLOSE.businessDay,
          takings: DAY_CLOSE.revenue,
          sessionsToday: running,
          sessionsNow: running,
          stationsInSession: running,
          stationsOffered: STATION_SEEDS.length,
          occupancyNowBasisPoints: STATION_SEEDS.length === 0 ? null : Math.round((running * 10_000) / STATION_SEEDS.length),
          openCalls: state.calls.length,
          computedAt: new Date().toISOString(),
        });
      }
      const health = FLOOR_PATHS.stationHealth.exec(pathname);
      if (health !== null) {
        const stationId = health[1] as string;
        const open = state.openAlerts.filter((alert) => alert.stationId === stationId);
        const recent = [...open, ...state.clearedAlerts.filter((alert) => alert.stationId === stationId)];
        const latest = stationId === floorStationId(4) ? (TELEMETRY[TELEMETRY.length - 1] ?? null) : null;
        // The settings the PC runs (F-STN-09): PC-04 reported the version the server builds now; the others have not reported.
        const policy =
          stationId === floorStationId(4)
            ? { currentVersion: 'pb1_current0001', appliedVersion: 'pb1_current0001', reportedAt: latest?.at ?? null, upToDate: true }
            : { currentVersion: 'pb1_current0001', appliedVersion: null, reportedAt: null, upToDate: null };
        const awake = open.filter((alert) => alert.snoozedUntil === undefined);
        return json(200, { stationId, status: awake.length > 0 ? 'unhealthy' : latest === null ? 'unknown' : 'healthy', open, recent, latest, policy });
      }
      const telemetry = FLOOR_PATHS.stationTelemetry.exec(pathname);
      if (telemetry !== null) {
        const stationId = telemetry[1] as string;
        return json(200, { stationId, samples: stationId === floorStationId(4) ? TELEMETRY : [] });
      }
      if (FLOOR_PATHS.needsAttention.test(pathname)) {
        return json(200, { alerts: state.openAlerts.filter((alert) => alert.snoozedUntil === undefined), commands: NEEDS_ATTENTION_COMMANDS });
      }
      const displayStatus = FLOOR_PATHS.stationDisplay.exec(pathname);
      if (displayStatus !== null) {
        if (!can('stations:manage')) return problem(403, 'forbidden', 'Your role does not allow this.');
        const link = state.displays.get(displayStatus[1] as string);
        return json(200, { link: link === undefined ? null : { id: link.id, stationId: displayStatus[1], createdAt: link.createdAt, lastSeenAt: link.lastSeenAt } });
      }
      return problem(405, 'method_not_allowed', 'Method not allowed');
    }

    // -- A staff-run station's countdown screen: link (or rotate) and revoke (ADR-0033) --------------
    const display = FLOOR_PATHS.stationDisplay.exec(pathname);
    if (display !== null) {
      if (!can('stations:manage')) return problem(403, 'forbidden', 'Your role does not allow this.');
      const stationId = display[1] as string;
      const seed = STATION_SEEDS.find((item) => floorStationId(item.number) === stationId);
      if (seed === undefined) return notFound('No such station at this venue.');
      if (method === 'DELETE') {
        state.displays.delete(stationId);
        return { status: 204, headers: {}, body: '' };
      }
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      if (seed.staffRun !== true) return problem(409, 'conflict', 'This station runs the station app: its own screen shows the time.');
      state.displaySerial += 1;
      const token = `Kq7vR2mX9pLs4TfW8nYc${two(state.displaySerial)}`;
      const link = { id: tid('sdl', `h${two(state.displaySerial)}`), token, createdAt: FLOOR_NOW, lastSeenAt: null };
      state.displays.set(stationId, link);
      return json(201, {
        link: { id: link.id, stationId, createdAt: link.createdAt, lastSeenAt: null },
        token,
        url: `https://app.clavian.io/display/${token}`,
      });
    }

    if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');

    // -- The start form's console price preview (ADR-0034) -------------------------------------------
    if (FLOOR_PATHS.consoleQuote.test(pathname)) {
      if (!can('sessions:start')) return problem(403, 'forbidden', 'Your role does not allow this.');
      const body = input as { stationId?: string; booking?: { controllers?: number; minutes?: number | null }; playerId?: string };
      const seed = STATION_SEEDS.find((item) => floorStationId(item.number) === body.stationId);
      if (seed === undefined) return notFound('No such station at this venue.');
      if (seed.staffRun !== true) return problem(409, 'conflict', 'A PC with the station app is funded, not booked.');
      const controllers = body.booking?.controllers ?? 1;
      if (controllers > seed.klass.defaultCapacity) return problem(409, 'conflict', `This station takes at most ${seed.klass.defaultCapacity} controllers.`);
      const minutes = body.booking?.minutes ?? null;
      const booking: FakeBooking = { blocks: [{ seconds: minutes === null ? null : minutes * 60, addedAt: FLOOR_NOW }], controllers: [{ fromSecond: 0, controllers, at: FLOOR_NOW }], saleId: null };
      return json(200, consoleQuoteDto(quoteBooking(booking, 0, body.playerId === undefined)));
    }

    // -- Staff start a session (F-SES-04), a staff-run one on a console (ADR-0033) ---------------------
    if (FLOOR_PATHS.startSession.test(pathname)) {
      if (!can('sessions:start')) return problem(403, 'forbidden', 'Your role does not allow this.');
      const body = input as {
        stationId?: string;
        playerId?: string;
        funding?: { kind?: string; minutes?: number; budgetAmount?: { amount: number } };
        booking?: { controllers: number; minutes: number | null };
        libraryEntryId?: string;
      };
      const seed = STATION_SEEDS.find((item) => floorStationId(item.number) === body.stationId);
      if (seed === undefined) return notFound('No such station at this venue.');
      if (runningOn(floorStationId(seed.number)) !== undefined) return problem(409, 'station_unavailable', 'This station already has a session.');
      // A staff-run station is booked (ADR-0034): controllers and a block, or Open — for a player or a walk-in.
      if (seed.staffRun === true) {
        if (body.booking === undefined || body.funding !== undefined) return problem(400, 'validation_failed', 'A staff-run station is booked, not funded.');
        if (body.booking.controllers > seed.klass.defaultCapacity) return problem(409, 'conflict', 'Too many controllers for this station.');
        state.startSerial += 1;
        const stationId = floorStationId(seed.number);
        const booking: FakeBooking = {
          blocks: [{ seconds: body.booking.minutes === null ? null : body.booking.minutes * 60, addedAt: FLOOR_NOW }],
          controllers: [{ fromSecond: 0, controllers: body.booking.controllers, at: FLOOR_NOW }],
          saleId: null,
        };
        const base: Session = {
          id: tid('ses', `s${two(state.startSerial)}`),
          organizationId: FLOOR_ORGANIZATION_ID,
          venueId: FLOOR_VENUE_ID,
          stationId,
          playerId: body.playerId ?? WALK_IN,
          status: 'active',
          startedAt: FLOOR_NOW,
          endedAt: null,
          endReason: null,
          currentSource: null,
          timeLeftSeconds: 0,
          fundedEndAt: null,
          plan: [],
          charged: { chargedSeconds: 0, chargedAmount: money(0) },
          serverTime: FLOOR_NOW,
          graceSeconds: 300,
          staffRun: true,
          ...(body.playerId === undefined ? { walkIn: true } : {}),
          ...(body.libraryEntryId === FLOOR_FC26_ENTRY_ID ? { staffTitle: { libraryEntryId: FLOOR_FC26_ENTRY_ID, name: 'EA SPORTS FC 26' } } : {}),
        };
        const fixture: SessionFixture = { session: withBooking(base, booking), booking, segments: [], events: [event('started', FLOOR_NOW, 'member', STAFF.sneha.id, { ...body.booking })] };
        state.sessions.push(fixture);
        return json(201, fixture.session);
      }
      const rate = RATE_PER_MINUTE[seed.klass.id] ?? 100;
      const seconds = body.funding?.kind === 'comp' ? (body.funding.minutes ?? 0) * 60 : Math.floor(((body.funding?.budgetAmount?.amount ?? 0) / rate) * 60);
      state.startSerial += 1;
      const stationId = floorStationId(seed.number);
      const source = body.funding?.kind === 'comp' ? 'comp' : 'postpaid';
      const session: Session = {
        id: tid('ses', `s${two(state.startSerial)}`),
        organizationId: FLOOR_ORGANIZATION_ID,
        venueId: FLOOR_VENUE_ID,
        stationId,
        playerId: body.playerId ?? player(0),
        status: 'active',
        startedAt: FLOOR_NOW,
        endedAt: null,
        endReason: null,
        currentSource: source,
        timeLeftSeconds: seconds,
        fundedEndAt: plusSeconds(FLOOR_NOW, seconds),
        plan: [{ source, label: null, seconds }],
        charged: { chargedSeconds: 0, chargedAmount: money(0) },
        serverTime: FLOOR_NOW,
        graceSeconds: 300,
      };
      state.sessions.push({ session, segments: [], events: [event('started', FLOOR_NOW, 'member', STAFF.sneha.id, { funding: source })] });
      state.presence.set(stationId, { online: true, mode: 'in_session', lastSeenAt: FLOOR_NOW, title: null });
      return json(201, session);
    }

    // -- Unpair a station's PC and remove a station (ADR-0057): `stations:manage`, refused while a session runs ----
    const change = FLOOR_PATHS.stationChange.exec(pathname);
    if (change !== null) {
      if (!can('stations:manage')) return problem(403, 'forbidden', 'Your role does not allow this.');
      const stationId = change[1] as string;
      const seed = seedsNow().find((item) => floorStationId(item.number) === stationId);
      if (seed === undefined) return notFound('No such station at this venue.');
      if (runningOn(stationId) !== undefined) return problem(409, 'conflict', 'A session is running on this station: end it first.');
      const wasPaired = station(seed).deviceId !== null;
      if (change[2] === 'unpair') {
        if (!wasPaired) return problem(409, 'conflict', 'No PC is paired to this station.');
        state.unpaired.add(stationId);
        return json(200, station(seed));
      }
      state.removed.add(stationId);
      return json(200, { id: stationId, number: seed.number, name: seed.name, removedAt: FLOOR_NOW, unpaired: wasPaired });
    }

    // -- Clearing or snoozing health alerts by hand (F-STN-11, 2026-10-02) --------------------------
    const clearing = FLOOR_PATHS.stationAlertsClear.exec(pathname);
    if (clearing !== null) {
      if (!can('stations:alerts_clear')) return problem(403, 'forbidden', 'Your role does not allow this.');
      const parsed = ClearStationAlertsRequestSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const { alertIds, note, snoozeHours } = parsed.data;
      const targets = state.openAlerts.filter((alert) => alert.stationId === clearing[1] && (alertIds === undefined || (alertIds as readonly string[]).includes(alert.id)));
      const changed = targets.map((alert): StationAlert => ({
        ...alert,
        clearedByMemberId: STAFF.sneha.id,
        ...(note === undefined ? {} : { clearNote: note }),
        ...(snoozeHours === undefined
          ? { clearedAt: FLOOR_NOW, clearReason: 'staff' as const }
          : { snoozedUntil: plusSeconds(FLOOR_NOW, snoozeHours * 3600) }),
      }));
      const ids = new Set(changed.map((alert) => alert.id));
      if (snoozeHours === undefined) {
        state.openAlerts = state.openAlerts.filter((alert) => !ids.has(alert.id));
        state.clearedAlerts = [...changed, ...state.clearedAlerts];
      } else {
        state.openAlerts = state.openAlerts.map((alert) => changed.find((item) => item.id === alert.id) ?? alert);
      }
      return json(200, { alerts: changed });
    }

    // -- Calls: "On my way" and "Done" (F-SES-25) -----------------------------------------------
    const callAction = FLOOR_PATHS.callAction.exec(pathname);
    if (callAction !== null) {
      const call = state.calls.find((item) => item.id === callAction[1]);
      if (call === undefined) return notFound('No such call at this venue.');
      if (call.state !== 'open' && call.state !== 'acknowledged') return problem(409, 'conflict', 'The call is already closed.');
      const next: StationCall =
        callAction[2] === 'acknowledge'
          ? { ...call, state: 'acknowledged', acknowledgedAt: FLOOR_NOW, acknowledgedByName: call.acknowledgedByName ?? STAFF.sneha.name }
          : { ...call, state: 'resolved', closedAt: FLOOR_NOW };
      state.calls = state.calls.map((item) => (item.id === call.id ? next : item));
      return json(200, next);
    }

    // -- A session's actions (F-SES-14/-16/-17/-20) ---------------------------------------------
    const action = FLOOR_PATHS.sessionAction.exec(pathname);
    if (action === null) return problem(405, 'method_not_allowed', 'Method not allowed');
    const fixture = findSession(action[1]);
    if (fixture === undefined) return notFound('No such session in this organisation.');
    if (!live(fixture)) return problem(409, 'session_already_ended', 'The session has already ended.');

    // A booked console session (ADR-0034): previews, a block, the controllers — and no comp or sold time.
    const booking = fixture.booking;
    if (booking !== undefined) {
      const capacity = LOUNGE.defaultCapacity;
      const elapsed = Math.max(0, secondsBetween(fixture.session.startedAt, FLOOR_NOW));
      const open = booking.blocks.some((block) => block.seconds === null);
      if (action[2] === 'comp' || action[2] === 'sell-time') return problem(409, 'conflict', 'A staff-run session is booked and paid once at the end.');
      if (action[2] === 'quote') {
        const body = input as { addMinutes?: number; controllers?: number };
        if (body.addMinutes !== undefined && open) return problem(409, 'conflict', 'An Open session has no end to move.');
        const next: FakeBooking = {
          ...booking,
          blocks: body.addMinutes === undefined ? booking.blocks : [...booking.blocks, { seconds: body.addMinutes * 60, addedAt: FLOOR_NOW }],
          controllers: body.controllers === undefined ? booking.controllers : [...booking.controllers.filter((change) => change.fromSecond < elapsed), { fromSecond: elapsed, controllers: body.controllers, at: FLOOR_NOW }],
        };
        const current = quoteBooking(booking, elapsed, fixture.session.walkIn === true);
        const after = quoteBooking(next, elapsed, fixture.session.walkIn === true);
        return json(200, { current: consoleQuoteDto(current), next: consoleQuoteDto(after), difference: money(after.total.amount - current.total.amount) });
      }
      if (action[2] === 'add-time') {
        if (open) return problem(409, 'conflict', 'An Open session has no end to move.');
        const minutes = (input as { minutes?: number }).minutes ?? 0;
        booking.blocks.push({ seconds: minutes * 60, addedAt: FLOOR_NOW });
        fixture.events.push(event('time_added', FLOOR_NOW, 'member', STAFF.sneha.id, { minutes }));
        fixture.session = withBooking({ ...fixture.session, status: fixture.session.status === 'grace' ? 'active' : fixture.session.status }, booking);
        return json(200, fixture.session);
      }
      if (action[2] === 'controllers') {
        const controllers = (input as { controllers?: number }).controllers ?? 1;
        if (controllers > capacity) return problem(409, 'conflict', `This station takes at most ${capacity} controllers.`);
        const from = booking.controllers.at(-1)?.controllers ?? 1;
        booking.controllers = [...booking.controllers.filter((change) => change.fromSecond < elapsed), { fromSecond: elapsed, controllers, at: FLOOR_NOW }];
        fixture.events.push(event('controllers_changed', FLOOR_NOW, 'member', STAFF.sneha.id, { from, to: controllers }));
        fixture.session = withBooking(fixture.session, booking);
        return json(200, fixture.session);
      }
      if (action[2] === 'end') {
        const ended = withBooking({ ...fixture.session, status: 'ended', endedAt: FLOOR_NOW, endReason: 'staff_ended' }, booking);
        const total = ended.booking?.quote.total.amount ?? 0;
        if (total > 0 && onConsoleOrder !== undefined) {
          booking.saleId = onConsoleOrder({
            sessionId: ended.id,
            stationId: ended.stationId,
            playerId: ended.walkIn === true ? null : ended.playerId,
            lines: (ended.booking?.quote.lines ?? []).map((line) => ({
              name: line.kind === 'first_controller' ? `Console ${line.blockMinutes ?? 0} min · 1st controller` : `Console ${line.blockMinutes ?? 0} min · extra controllers`,
              unitPrice: line.unitAmount.amount,
              quantity: line.quantity,
            })),
            discount: ended.booking?.quote.discount.amount ?? 0,
          });
        }
        fixture.session = withBooking({ ...fixture.session, status: 'ended', endedAt: FLOOR_NOW, endReason: 'staff_ended' }, booking);
        fixture.events.push(event('ended', FLOOR_NOW, 'member', STAFF.sneha.id, { endReason: 'staff_ended' }));
        if (booking.saleId !== null) fixture.events.push(event('order_placed', FLOOR_NOW, 'member', STAFF.sneha.id, { saleId: booking.saleId }));
        return json(200, fixture.session);
      }
    }

    switch (action[2]) {
      case 'comp': {
        const parsed = CompSessionSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        addTime(fixture, parsed.data.minutes, 'comp');
        fixture.events.push(
          event('comped', FLOOR_NOW, 'member', STAFF.sneha.id, { minutes: parsed.data.minutes, reason: parsed.data.reason, ...(parsed.data.note === undefined ? {} : { note: parsed.data.note }) }),
        );
        return json(200, fixture.session);
      }
      case 'sell-time': {
        const parsed = SellSessionTimeSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        addTime(fixture, parsed.data.minutes, 'postpaid');
        fixture.events.push(event('sold_time', FLOOR_NOW, 'member', STAFF.sneha.id, { minutes: parsed.data.minutes }));
        return json(200, fixture.session);
      }
      case 'lock': {
        // ADR-0033: staff lock a staff-run session; a PC's locks from the Floor's Lock, which tells the PC.
        if (fixture.session.staffRun !== true) return problem(409, 'conflict', 'This session is on a PC with the station app.');
        const locked = (input as { locked?: boolean }).locked === true;
        if (locked && fixture.session.status === 'active') fixture.session = { ...fixture.session, status: 'locked' };
        if (!locked && fixture.session.status === 'locked') fixture.session = { ...fixture.session, status: 'active' };
        fixture.events.push(event(locked ? 'locked' : 'unlocked', FLOOR_NOW, 'member', STAFF.sneha.id, locked ? { reason: 'remote' } : { method: 'staff' }));
        return json(200, fixture.session);
      }
      case 'message': {
        const parsed = MessageSessionSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const entry = event('message_sent', FLOOR_NOW, 'member', STAFF.sneha.id, { text: parsed.data.text, needsAck: parsed.data.needsAck ?? false });
        fixture.events.push(entry);
        return json(200, entry);
      }
      default: {
        // End: settle the open segments at now, free the station.
        fixture.segments = fixture.segments.map((item) => (item.endedAt === null ? { ...item, endedAt: FLOOR_NOW } : item));
        fixture.session = { ...fixture.session, status: 'ended', endedAt: FLOOR_NOW, endReason: 'staff_ended', currentSource: null, timeLeftSeconds: 0, fundedEndAt: null, plan: [] };
        fixture.events.push(event('ended', FLOOR_NOW, 'member', STAFF.sneha.id, { endReason: 'staff_ended' }));
        if (fixture.session.staffRun !== true) state.presence.set(fixture.session.stationId, { online: true, mode: 'locked', lastSeenAt: FLOOR_NOW, title: null });
        return json(200, fixture.session);
      }
    }
  }

  /** Make it station: a new station on the floor, a PC with the app pairing (`pairing`) or a Boot-only one (no app). */
  function addStation(spec: FakeMadeStation): { readonly station: Station } | { readonly conflict: string } | { readonly missing: string } {
    if (seedsNow().some((seed) => seed.number === spec.number)) {
      return { conflict: `Another station already has number ${spec.number}.` };
    }
    const klass = FLOOR_CLASSES.find((one) => one.id === spec.classId);
    if (klass === undefined) return { missing: 'No such station class at this venue.' };
    const zone = spec.zoneId === undefined ? null : (FLOOR_ZONES.find((one) => one.id === spec.zoneId) ?? undefined);
    if (zone === undefined) return { missing: 'No such zone at this venue.' };
    const seed: StationSeed = { number: spec.number, name: spec.name ?? null, klass, zone, presence: { online: false, mode: 'pairing', lastSeenAt: null, title: null } };
    state.made.push(seed);
    state.presence.set(floorStationId(seed.number), { ...seed.presence });
    return { station: station(seed) };
  }

  /** The class of a station on the floor (Clavian Boot's fake resolves a PC's image through it, F-BOOT-27). */
  function classOf(stationId: string): string | undefined {
    return [...STATION_SEEDS, ...state.made].find((seed) => floorStationId(seed.number) === stationId)?.klass.id;
  }

  return { state, handle, addStation, classOf };
}

export type FakeFloor = ReturnType<typeof createFakeFloor>;
